import { reactive, readonly, watch, toRefs, computed, inject, provide } from 'vue'
import { useRoute } from 'vue-router'

import useAuth from '@/composables/auth/useAuth'
import { type DefaultItem } from '@/types/item'
import { type LocaleTextType, type LocaleCodeType, type Localization, type LocalizationFragmentKeyData, type LocalizationLink } from '@/types/Localization'
import { getLocalizationNameKey, replaceLinkFormat } from '@/utils/localization'
import { isNil, isStrNil } from '@/utils/validation'

export interface State {
  loginLocaleCode: LocaleCodeType
  data: Localization[]
}

export const LANGUAGE_ITEMS: Array<DefaultItem<LocaleCodeType>> = [
  {
    text: 'English',
    value: 'en'
  },
  {
    text: 'ไทย',
    value: 'th'
  }
]

const STATE_NAME = 'armLocalization'

const getDefaultInitState = (): State => {
  return {
    loginLocaleCode: 'en',
    data: []
  }
}

const getDefaultState = (): State => {
  const state = localStorage.getItem(STATE_NAME)

  if (state !== null) {
    return JSON.parse(state)
  }

  return getDefaultInitState()
}

const state = reactive<State>(getDefaultState())

// #region Actions

export const setLoginLocaleCode = (value: LocaleCodeType): void => {
  if (value === state.loginLocaleCode) {
    return
  }

  state.loginLocaleCode = value
}

export const setLocalizationData = (value: Localization[]): void => {
  state.data = value
}

export const getLocalizationData = (): Localization[] => {
  return state.data
}

export const clearLocalization = (): void => {
  state.data = []
}

const localeCodeToText = (value: LocaleCodeType): LocaleTextType => {
  if (value === 'en') {
    return 'english'
  }

  return 'thai'
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useBaseLocalization = () => {
  const route = useRoute()
  const auth = useAuth()

  // #region Getters

  const localeCode = computed((): LocaleCodeType | undefined => {
    if (typeof route.query.lg === 'string' && route.query.lg !== '') {
      return route.query.lg as LocaleCodeType
    }

    return !auth.loggedIn ? state.loginLocaleCode : auth.userProfile?.language_preference
  })

  const localeTexts = computed((): Record<string, string> => {
    if (isNil(localeCode.value)) {
      return {}
    }

    const localeText = localeCodeToText(localeCode.value)

    return state.data.reduce<Record<string, string>>((localeTexts, localeData) => {
      const text = localeData[localeText]
      return {
        ...localeTexts,
        [localeData.key]: isStrNil(text) ? localeData.english : text
      }
    }, {})
  })

  const localizationNameKey = computed((): string => {
    if (isNil(localeCode.value)) {
      return ''
    }

    return getLocalizationNameKey(localeCode.value)
  })

  // #endregion

  // #region Actions

  const setData = (value: Localization[]): void => {
    state.data = value
  }

  const addData = (value: Localization): void => {
    state.data = [...state.data, value]
  }

  const translationFragment = (key: string, ...fragments: Array<number | string | null | undefined>): string => {
    let localeText: string | undefined = localeTexts.value[key]

    for (let index = fragments.length - 1; index >= 0; index--) {
      const re = new RegExp(`%${index + 1}`, 'g')
      localeText = localeText?.replace(re, `${fragments[index]}`)
    }

    return localeText
  }

  const translationWithFragmentKeys = (key: string, fragmentKeys: LocalizationFragmentKeyData[]): string => {
    let localeText: string | undefined = localeTexts.value[key]

    for (let index = fragmentKeys.length - 1; index >= 0; index--) {
      const fragmentKeyData = fragmentKeys[index]
      const re = new RegExp(`{{${fragmentKeyData.fragment_key}}}`, 'g')

      const replaceValue = typeof fragmentKeyData.value === 'object' && fragmentKeyData.value !== null
        ? localeTexts.value[fragmentKeyData.value.translation_key]
        : fragmentKeyData.value

      localeText = localeText?.replace(re, `${replaceValue}`)
    }

    return localeText
  }

  const translationWithLinks = (key: string, localizationLinks: LocalizationLink[]): JSX.Element | undefined => {
    const localeText: string | undefined = localeTexts.value[key]

    return localeText !== undefined ? replaceLinkFormat(localeText, localizationLinks) : undefined
  }

  const reset = (): void => {
    state.data = []
  }

  // #endregion

  // #region Watchers

  watch(
    () => state,
    (newState) => {
      localStorage.setItem(STATE_NAME, JSON.stringify(newState))
    },
    { deep: true }
  )

  // #endregion

  if (localStorage.getItem(STATE_NAME) === null) {
    localStorage.setItem(STATE_NAME, JSON.stringify(state))
  }

  return {
    ...toRefs(readonly(state)),
    // Getters
    localeTexts,
    localizationNameKey,
    // Actions
    setLoginLocaleCode,
    setData,
    addData,
    translationFragment,
    translationWithFragmentKeys,
    translationWithLinks,
    reset
  }
}

export const defaultKey = Symbol('global:localization')

export default function useLocalization (): ReturnType<typeof useBaseLocalization> {
  const result = inject<ReturnType<typeof useBaseLocalization> | undefined>(defaultKey, undefined)

  if (result === undefined) {
    const newResult = useBaseLocalization()
    provide(defaultKey, newResult)
    return newResult
  }

  return result
}
